import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Award, 
  Target, 
  Heart, 
  Shield, 
  Users, 
  Building2,
  Microscope,
  Factory,
  CheckCircle2,
  Droplets,
  Pill,
  FlaskConical
} from 'lucide-react';

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">About Kindred Pharmaceuticals</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Your trusted partner in quality pharmaceutical solutions. Specializing in oral liquid formulations 
              that make healthcare accessible and effective for all.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <Card className="border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent">
              <CardTitle className="flex items-center gap-3 text-2xl">
                <Target className="h-8 w-8 text-blue-600" />
                Our Mission
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed text-lg">
                To provide high-quality, accessible oral liquid pharmaceutical formulations that meet 
                the diverse needs of patient populations, ensuring effective treatment with convenience 
                and palatability.
              </p>
            </CardContent>
          </Card>

          <Card className="border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent">
              <CardTitle className="flex items-center gap-3 text-2xl">
                <Heart className="h-8 w-8 text-blue-600" />
                Our Vision
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed text-lg">
                To be the leading pharmaceutical company recognized for innovation in oral liquid 
                formulations, setting industry standards in quality, compliance, and patient care.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at Kindred Pharmaceuticals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
            {[
              { icon: Shield, title: "Quality", desc: "Uncompromising standards" },
              { icon: CheckCircle2, title: "Integrity", desc: "Ethical in all practices" },
              { icon: Heart, title: "Patient-Centric", desc: "Health comes first" },
              { icon: FlaskConical, title: "Innovation", desc: "Continuous improvement" },
              { icon: Award, title: "Compliance", desc: "Regulatory excellence" },
              { icon: Users, title: "Reliability", desc: "Consistent delivery" }
            ].map((value, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all hover:-translate-y-1">
                <CardContent className="pt-6 pb-6">
                  <div className="flex justify-center mb-4">
                    <div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center">
                      <value.icon className="h-7 w-7 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Specialization */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Specialization</h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Kindred Pharmaceuticals specializes in oral liquid formulations, addressing the growing 
              market demand for convenient, palatable, and easily administrable medications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Droplets, title: "Syrups", desc: "Sweetened liquid formulations" },
              { icon: FlaskConical, title: "Suspensions", desc: "Particle-based solutions" },
              { icon: Droplets, title: "Drops", desc: "Concentrated dosage forms" },
              { icon: Pill, title: "Elixirs", desc: "Clear flavored solutions" }
            ].map((product, index) => (
              <Card key={index} className="border-blue-200 hover:shadow-xl transition-all hover:-translate-y-2">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                      <product.icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">{product.title}</h3>
                  <p className="text-gray-600">{product.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-8 border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed text-center">
                Our oral liquid formulations are specially designed to cater to patient segments requiring 
                ease of administration, including <strong>pediatric</strong> and <strong>geriatric</strong> populations. 
                We focus on conditions like cough, cold, allergy relief, pain management, and nutritional supplementation.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quality & Compliance */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Quality Assurance & Compliance</h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                At Kindred Pharmaceuticals, quality is not just a goal—it's our commitment to every patient we serve.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-blue-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-green-50 to-transparent">
                  <CardTitle className="flex items-center gap-3">
                    <Microscope className="h-6 w-6 text-green-600" />
                    Advanced Testing
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">State-of-the-art in-house laboratories with cutting-edge testing technologies</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Rigorous testing of starting materials and finished products</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Adherence to Good Lab Practices (GLP) standards</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-blue-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent">
                  <CardTitle className="flex items-center gap-3">
                    <Award className="h-6 w-6 text-blue-600" />
                    Certifications & Standards
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Compliant with Indian Pharmaceutical standards of <strong>"Revised Schedule-M"</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Actively pursuing <strong>WHO-GMP</strong> certifications</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Adherence to Good Manufacturing Practices (GMP)</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities & Modernization */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">State-of-the-Art Facilities</h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Our facilities are undergoing significant modernization to meet stringent WHO-GMP manufacturing standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <Card className="border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-transparent">
                <CardTitle className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-purple-600" />
                  Manufacturing Excellence
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Air Handling Units and HVAC systems with 0.3-micron particulate filters</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Clean room areas with high-density PUFF insulation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Manufacturing vessels to cGMP specifications with reinforced integrity</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Water generation systems for 100% demineralized water</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-transparent">
                <CardTitle className="flex items-center gap-3">
                  <Factory className="h-6 w-6 text-amber-600" />
                  Capacity & Growth
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>Investment in new, automated filling lines for increased capacity</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>Climate-controlled warehouses for optimal storage</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>Strategic partnership with sister company, Kertake Healthcare</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>Expertise in leveraging pharmaceutical supply chain from APIs to packaging</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-blue-300 bg-gradient-to-br from-blue-50 to-white shadow-lg">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <Factory className="h-8 w-8 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Sister Company: Kertake Healthcare</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our wholly owned sister company, <strong>Kertake Healthcare</strong>, plays a vital role in our 
                    manufacturing capacity and operational excellence. This strategic partnership enables us to 
                    significantly boost production volumes and maintain the highest quality standards across all operations.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Why Choose Kindred Pharmaceuticals?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="font-bold text-xl mb-2">Quality First</h3>
                <p className="text-blue-100">
                  Uncompromising quality standards with rigorous testing at every stage
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="font-bold text-xl mb-2">Patient-Centric</h3>
                <p className="text-blue-100">
                  Formulations designed for ease of use and patient comfort
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Award className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="font-bold text-xl mb-2">Compliance</h3>
                <p className="text-blue-100">
                  Meeting and exceeding regulatory standards for your safety
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};